import { CORRUPTION_TIERS, type CorruptionTier, type Unit } from "@sins2-atlas/game-data";
import type { CorruptionState, StatFrame } from "./frame";
import { BASE_LEVEL } from "./level";
import { ABILITY_LEVEL_WORDS } from "./vocabulary";

// The frame in words: every declaration, control label and rule sentence a surface prints
// about the frame it is showing. Display vocabulary, not URL grammar — split out of frame.ts
// on 2026-09-08 beside lib/vocabulary.ts and lib/disclosures.ts, which is where the standing
// rule says reader-facing prose lives.

// The game's own adjectives (tooltip.ability.has_corruption_low/medium/high: "With Mild /
// Moderate / Extreme Corruption:"). The noun "tier" never reaches the UI (CONTEXT.md:
// Corruption Tier). ONE table (review 2026-09-08: the Corruption control kept its own copy of
// the three words): the display compound below and the control's option list both derive
// from it, so the two cannot spell a tier differently.
const CORRUPTION_ADJECTIVES: Record<CorruptionTier, string> = {
  mild: "Mild",
  moderate: "Moderate",
  extreme: "Extreme",
};

/** The tier's display compound, "Mild Corruption" — the same compound the dataset's cell
 * captions carry — or undefined when uncorrupted. */
export function corruptionLabel(corruption: CorruptionState): string | undefined {
  return corruption === "none" ? undefined : `${CORRUPTION_ADJECTIVES[corruption]} Corruption`;
}

/** The Corruption control's word for a state: the adjective alone, or "None" for the absence. */
export function corruptionStateLabel(state: CorruptionState): string {
  return state === "none" ? "None" : CORRUPTION_ADJECTIVES[state];
}

/** The axis's states as the Corruption control lists them: the absence first, then the game's
 * three tiers in the game's order, each under corruptionStateLabel's word. The trigger and the
 * options read this one list, so the closed control cannot spell a tier the open list spells
 * differently (review 2026-09-08: it printed the URL token, "mild" beside "Mild"). */
export const CORRUPTION_STATES: ReadonlyArray<{ value: CorruptionState; label: string }> = [
  "none" as const,
  ...CORRUPTION_TIERS,
].map((value) => ({ value, label: corruptionStateLabel(value) }));

/** The research axis in words, for the Tech page's "what you have built" line. A Tech Set
 * names its FULL size, not the shorter list the URL carries: the count is what actually
 * moves the numbers, so every prerequisite and every implied Tier is in it. */
export function researchLabel(frame: StatFrame): string {
  if (frame.tech !== undefined) return `custom tech (${frame.tech.size})`;
  return frame.research === "fullyTeched" ? "fully-teched stats" : "base stats";
}

/** The Tech control's trigger, which states what is applied without being opened (ADR 0011,
 * Issue 38): the set's two extents in the app's own words — Base is the empty set,
 * Fully-Teched the full one — and otherwise the set's whole size against the picker's rows
 * for the Races on the page ("Tech: 6 of 39"). The size is the SET's, not the URL's shorter
 * maximal list, because the members are what move the numbers. The Corruption axis is not in
 * it: it is its own control (ADR 0007), and filing it under Tech is the one thing this
 * control must not say. */
export function techControlLabel(frame: StatFrame, rowCount: number): string {
  if (frame.tech !== undefined) return `Tech: ${frame.tech.size} of ${rowCount}`;
  return frame.research === "fullyTeched" ? "Tech: fully-teched" : "Tech: base";
}

/** The Loadout control's trigger, which states what is equipped without being opened
 * (Issue 46, ADR 0012): the Item Slots filled against the Item Slots the hull has. The cap
 * is in it because it is the rule the control enforces, and a reader who cannot see it has
 * no way to know why the next tick will be refused. Says "Items", the game's own word for
 * the shop tab these come from, never "Loadout" on the control itself. */
export function loadoutControlLabel(equipped: number, capacity: number): string {
  return `Items: ${equipped} of ${capacity}`;
}

/** The Level control's trigger, which states the level without being opened (Issue 50) — the
 * game's own word, and the number the hull is actually read at. Level 1 says so rather than
 * going blank: the trigger is the only place the page states the level before the reader
 * opens anything, and a control naming nothing is one they cannot read. */
export function levelControlLabel(level: number | undefined): string {
  return `Level: ${level ?? BASE_LEVEL}`;
}

/** The Ability Points lever's trigger, which states the pool without being opened (ADR 0015,
 * PRD story 4) — the game's own words for the lever, and the points spent against the points
 * the hull has at the Level in force. Nothing spent says the lever's name alone: a control
 * reading "0 of 3" on every unvisited hull would put a figure where there is nothing to
 * report. The pool's size is ASSUMED (lib/ability-points.ts), which the panel says where the
 * reader can act on it; the trigger states the count, as the Items trigger states the cap. */
export function abilityPointsControlLabel(spent: number, pool: number): string {
  return spent === 0 ? "Ability Points" : `Ability Points: ${spent} of ${pool}`;
}

/** The Ability Points panel in words (Issue 62). Two of them are the GAME's and are quoted
 * as it writes them — "Requires Level 5" is the prefix its own ability tooltips carry, and
 * "No ability points available" is its own sentence when the pool is empty (CONTEXT.md:
 * Ability Points). The rest are the site's, and each states a fact and stops: the panel
 * reports the pool and what a position would cost, and never explains how the game's point
 * system works (the standing rule — the atlas presents data). */
export const ABILITY_POINT_WORDS = {
  /** The panel's own header line, under the trigger that already names the lever. */
  poolLine: (spent: number, pool: number) => `${spent} of ${pool} spent`,
  /** Why a position the hull's Level has not reached cannot be bought — the game's prefix,
   * naming the Level the ability's own gate list states for that position. The Abilities
   * card marks the same fact with the same words, so it is declared once, there. */
  gated: ABILITY_LEVEL_WORDS.locked,
  /** Why a position the pool cannot reach cannot be bought — the game's own sentence. */
  unaffordable: "No ability points available",
  /** A position button's accessible name: the ability it belongs to, then the Ability Level
   * it sets. Position 0 is Unspent — our word, and the one the Abilities card already marks
   * that state with (lib/vocabulary.ts), so a reader meets one word for one state. */
  position: (name: string, position: number) =>
    position === 0
      ? `${name}: ${ABILITY_LEVEL_WORDS.unspent}`
      : `${name}: Ability Level ${position}`,
  /** The marked position's title, in the words the Abilities card's caption uses. */
  inForceTitle: ABILITY_LEVEL_WORDS.inForceTitle,
  /** The panel's quiet actions. Clearing is offered and spending everything is not: the site
   * does not choose a build (ADR 0015's out-of-scope list), and taking the reader's own
   * choices back off is the Loadout panel's "Take all off" for the next lever. */
  unspendAll: "Unspend all",
  close: "Close",
} as const;

/** What lowering the Level took out of the allocation, said out loud (ADR 0015 decision 2,
 * Issue 61) — the sentence that joins the Loadout's own removals in the one notice a per-hull
 * control writes. It names the Level that did it, then each ability the trim moved in the
 * game's own order: the Ability Level it kept, or "removed" where it kept none, because zero
 * is Unspent (CONTEXT.md) and "dropped to 0" would state a position the game does not offer.
 *
 * `undefined` when the trim moved nothing, which is what makes a Level change that leaves the
 * allocation legal — and every raise — say nothing at all. */
export function abilityPointsTrimNote(
  level: number,
  trimmed: readonly { name: string; to: number }[],
): string | undefined {
  if (trimmed.length === 0) return undefined;
  const said = trimmed.map((change) =>
    change.to === 0 ? `${change.name} removed` : `${change.name} dropped to ${change.to}`,
  );
  return `Level ${level}: ${said.join(", ")}.`;
}

/** How a Corruption's bonus composes, said once in the roster's note (ADR 0007): the
 * observed rule is (base + additives) × (1 + summed scalars), and the tier buff is one more
 * scalar in that sum. Says "Corruption", never "tier" (CONTEXT.md). */
export const CORRUPTION_SUM_RULE =
  "A Corruption's percentage is added to the research percentages, then applied once.";

/** The roster's "how are these stats computed" opener. It says only what the Tech and
 * Corruption controls beside it do not: the two per-hull levers (ADR 0012) are at rest on
 * every row, and a hull's own page can move them. Rewritten 2026-09-08 from a frame-aware
 * paragraph that restated the controls. */
export const ROSTER_STATS_NOTE =
  "Every row is the hull at level 1 with no ship items equipped and no ability points spent. " +
  "A unit's own page can set a Level, equip items and spend ability points, which change its " +
  `stats and price. ${CORRUPTION_SUM_RULE}`;

/** The Corruption the URL names that the opened unit cannot carry — a strikecraft or Envoy
 * has no pool, so the tier moves nothing on its page. Returned as the display compound for
 * the page to say so (review round 2026-09-05: the declaration had claimed the tier with no
 * notice); undefined when uncorrupted or when the unit has a pool. */
export function unhonouredCorruption(unit: Unit, frame: StatFrame): string | undefined {
  if (unit.corruption !== undefined) return undefined;
  return corruptionLabel(frame.corruption);
}
